import { useFormContext, Controller, type FieldValues, type Path } from 'react-hook-form';
import { Input } from 'pink-lava-ui';
import { Select } from 'antd';
import { useState, type ReactNode } from 'react';
import type { CommonInputProps } from 'pink-lava-ui/types';
import type { roundedType } from 'pink-lava-ui/types';
import { Eye, EyeClosed, SearchIcon } from 'lucide-react';

export type SearchOption = {
  name: string;
  label: string;
  placeholder?: string;
};

export const TextInput = <T extends FieldValues = any>({
  name,
  label,
  placeholder,
  disabled,
  required,
  showCount,
  maxLength,
  type = 'text',
  validate,
  onChange,
  onChangeCallback,
  prefix,
  suffix,
  showErrorMessage = true,
  size = 'middle',
  className = '',
  rounded,
  variant = 'outlined',
  searchOptions,
  onSearchOptionChange,
  selectedSearchOption,
  info,
  autoComplete,
}: {
  name: string | Path<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  showCount?: boolean;
  maxLength?: number;
  type?: CommonInputProps['type'] | 'search';
  onChange?: (_value: any, _fieldOnChange: (_value: any) => void) => void;
  onChangeCallback?: (_value: any, _setValue: (_name: keyof T & string, _value: any) => void) => void;
  validate?: (_value: any) => boolean | string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  showErrorMessage?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
  rounded?: roundedType;
  variant?: 'outlined' | 'filled' | 'borderless' | 'underlined';
  searchOptions?: SearchOption[];
  onSearchOptionChange?: (_option: SearchOption) => void;
  selectedSearchOption?: SearchOption;
  info?: string;
  autoComplete?: string;
}) => {
  console.log({ info });
  const { control, setValue } = useFormContext();

  // State for selected search option
  const [internalSelectedSearchOption, setInternalSelectedSearchOption] = useState<SearchOption | undefined>(
    searchOptions?.[0]
  );
  const currentSelectedSearchOption = selectedSearchOption || internalSelectedSearchOption;

  // Use dynamic field name based on selected search option
  const dynamicFieldName = currentSelectedSearchOption?.name || name;
  // Removed auto-focus effect to prevent unfocusing input when error messages appear/disappear

  // Handle search option change
  const handleSearchOptionChange = (value: string) => {
    const option = searchOptions?.find(opt => opt.name === value);
    if (option) {
      setInternalSelectedSearchOption(option);
      onSearchOptionChange?.(option);
      // Clear the input value when search option changes
      setValue(dynamicFieldName as string, '');
    }
  };

  // Determine input class based on rounded prop
  const inputClassName = `${rounded === 'large' ? '!rounded-full' : rounded ? '!rounded-lg' : ''} ${className} ${type === 'search' && !searchOptions ? 'pl-4! pr-3!' : ''}`;

  // Determine suffix based on type
  const determinedSuffix = type === 'search' && !suffix ? <SearchIcon size={18} className="text-gray-400" /> : suffix;

  // Determine placeholder based on selected search option or default
  const determinedPlaceholder =
    currentSelectedSearchOption?.placeholder || placeholder || (type === 'search' ? 'Search...' : undefined);

  // Create addonBefore for search options
  const addonBefore =
    searchOptions && searchOptions.length > 0 ? (
      <Select
        defaultActiveFirstOption
        value={currentSelectedSearchOption?.name}
        onChange={handleSearchOptionChange}
        size={size}
        options={searchOptions.map(option => ({
          value: option.name,
          label: option.label,
        }))}
      />
    ) : undefined;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 font-nunito">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Controller
        name={dynamicFieldName as string}
        control={control}
        rules={{
          required: required ? `${label || 'This field'} is required` : false,
          validate: value => {
            if (type === 'number' && value !== '' && isNaN(Number(value))) {
              return `${label || 'This field'} must be a valid number`;
            }
            if (typeof validate === 'function') {
              const customResult = validate(value);
              if (customResult !== true) return customResult;
            }
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => {
          return (
            <>
              <Input
                {...field}
                placeholder={determinedPlaceholder}
                disabled={disabled}
                autoComplete={autoComplete}
                required={required}
                type={type === 'search' ? 'text' : (type as any)}
                showCount={showCount}
                maxLength={maxLength}
                size={size}
                className={`${inputClassName} font-nunito! ${error ? 'border-red-500!' : ''}`}
                onChange={(e: any) => {
                  if (onChange) {
                    // Scenario 1: onChange overrides default behavior
                    onChange(e, field.onChange);
                  } else {
                    // Default behavior: update form field
                    field.onChange(e);
                    // Scenario 2: onChangeCallback executes alongside default behavior
                    onChangeCallback?.(e, setValue);
                  }
                }}
                variant={type === 'search' && addonBefore ? 'borderless' : variant}
                prefix={prefix as any}
                suffix={determinedSuffix as any}
                addonBefore={addonBefore}
                iconRender={
                  type === 'password' ? visible => (visible ? <EyeClosed size={21} /> : <Eye size={21} />) : undefined
                }
              />
              {info && <p className="text-gray-500 text-xs mt-1 whitespace-pre-line">{info}</p>}
              {showErrorMessage && error?.message && <p className="text-red-500 text-xs mt-1">{error?.message}</p>}
            </>
          );
        }}
      />
    </div>
  );
};
